/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import {
  AbstractFormComponent,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  JobFinderDialogConfig,
  jobFinderDialogRoute,
  JobFinderListItem,
  PbHousePlanFinderDialogConfig,
  pbHousePlanFinderDialogRoute,
  PbHousePlanFinderListItem,
} from '@gh/builder-shared';
import { ActionFlow, getSingleNewSelectedEntity, streamValueAction } from '@gh/common-shared';
import { isMessageBasket } from '@gh/core-messages';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  getControl,
  WaitingService,
  yesAnswer,
  yesCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { throwError$ } from '@gh/rx';
import { catchError$, filter$, map$, mapTo$, switchMap$ } from '@gh/rx/operators';
import { isNil } from 'lodash';
import { JobRedoBusinessService } from '../../resources/job-redo-business.service';
import { JobRedoDetails } from '../../resources/job-redo.model';
import { JobRedoEstimateFormState } from './job-redo-estimate-form.service';

@Component({
  selector: 'gh-job-redo-estimate',
  template: require('./job-redo-estimate.component.html'),
  styles: [require('./job-redo-estimate.component.scss')],
  viewProviders: [
    JobRedoEstimateFormState,
  ],
})
export class JobRedoEstimateComponent extends AbstractFormComponent<JobRedoDetails> implements OnInit {

  constructor(public form: JobRedoEstimateFormState,
              private waitings: WaitingService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private businessService: JobRedoBusinessService,
              private dialogService: DialogService,
              public userContext: BuilderUserContext) {
    super();
  }

  get housePlan(): string {
    const { job } = this.form;
    return job ? `${job.housePlanCode}: ${job.housePlanName}` : '';
  }

  ngOnInit(): void {
    this.form.build();
  }

  get completeFlow(): ActionFlow<FormFlowState<JobRedoDetails>> {
    return this.pipelines.complete({
      execute: streamValueAction((state: FormFlowState<JobRedoDetails>) =>
        this.businessService.redo(state.form.submitValue).pipe(
          this.waitings.operator(),
          catchError$((error) => {
            // if error is msg_redo_delete_construction_schedule
            // we have to ask user if he wants to delete associated construction schedule
            if (isMessageBasket(error) && error.has('msg_redo_delete_construction_schedule')) {
              return this.dialogService.confirmation(yesCancelConfirmation('msg_redo_delete_construction_schedule')).pipe(
                filter$(yesAnswer),
                switchMap$(() => this.businessService.redo(state.form.submitValue, true)
                  .pipe(this.waitings.operator())));
            } else {
              return throwError$(error);
            }
          }),
          mapTo$(state))),
      finish: () => this.form.reset(),
    });
  }

  hpDescription(hp: PbHousePlanFinderListItem): string {
    return `${hp.code}: ${hp.name}`;
  }

  onReset(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.reset());
  }

  onJobSearch(): void {
    this.dialogService.openWithInput(jobFinderDialogRoute, <JobFinderDialogConfig>{
      preselected: this.form.jobId ? [this.form.jobId] : void 0,
      multiselection: false,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((job: JobFinderListItem) => this.form.setJob(job));
  }

  onJobClear(): void {
    this.form.reset();
  }

  onPriceBookChanged(): void {
    this.form.priceBookChanged();
  }

  onHousePlanSearch(): void {
    if (!this.form.isPriceBookSelected) {
      return;
    }

    const priceBook = this.form.priceBook;

    this.dialogService.openWithInput(pbHousePlanFinderDialogRoute, <PbHousePlanFinderDialogConfig>{
      priceBookId: isNil(priceBook) ? void 0 : priceBook.id, // tslint:disable-line:no-non-null-assertion
      priceBookName: isNil(priceBook) ? void 0 : priceBook.name, // tslint:disable-line:no-non-null-assertion
      filter: { isSalesEstimate: true, isQuickQuote: getControl(this.form.group, 'showQQ').value },
      preselected: this.form.housePlan ? [this.form.housePlan.housePlanId] : void 0,
      multiselection: false,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((hp: PbHousePlanFinderListItem) => this.form.setHousePlan(hp));
  }

  loadPriceBook(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findPriceBooksByName({
      ...event.query,
    }));
  }
}
